import { ReactNode } from 'react';
import { RadioGroup, RadioGroupProps } from '@anyui/core';
import { cn } from '@anytag/shared-utils';
import { Option } from '@anytag/shared-types';
import { Radio } from '@anytag/shared-atoms';
import { RHFFieldWrapper } from './shared';
import { DefaultFieldProps } from './types';
import { useFormatRHFFieldProps } from './hooks';

interface RadioOptions<V, L> extends Option<V, L> {
  help?: ReactNode;
  children?: ReactNode;
}

export interface RadiosFieldProps<T extends string = string, V = string>
  extends Omit<DefaultFieldProps<T, V>, 'onChange'> {
  options?: RadioOptions<V, ReactNode>[];
  label?: ReactNode;
  itemsPerRow?: number;
  direction?: RadioGroupProps['direction'];
  onChange?: (name: T, value: V, options: RadioOptions<V, ReactNode>[]) => void;
}

export const RadiosField = <T extends string, V = string>(props: RadiosFieldProps<T, V>) => {
  const {
    fieldValue,
    setValueOptions,
    fieldWrapperProps,
    formContext: { setValue },
    fieldProps: { name, disabled, onChange, direction = 'horizontal', itemsPerRow, options = [] }
  } = useFormatRHFFieldProps(props);

  const allowCustomItemPerRow = !!itemsPerRow && itemsPerRow > 1 && direction === 'horizontal';

  return (
    <RHFFieldWrapper {...fieldWrapperProps}>
      <RadioGroup
        direction={direction}
        value={fieldValue as string}
        style={{ ['--item-size' as string]: `calc(100%/${itemsPerRow || 1})` }} // Support itemsPerRow
        className={cn({ '[&_[role="radiogroup"]]:flex-wrap': allowCustomItemPerRow })} // Support itemsPerRow
        onChange={(value) =>
          onChange ? onChange(name, value as V, options) : setValue<any>(name, value as V, setValueOptions)
        }
      >
        {options.map(({ value, help, ...restOptionProps }, index) => (
          <Radio
            hint={help}
            key={value as string}
            value={value as string}
            disabled={restOptionProps.disabled || disabled}
            className={cn({
              // Support itemsPerRow
              'w-(--item-size) basis-(--item-size) [&+[data-slot=field]]:ml-0!': allowCustomItemPerRow,
              '[&+[data-slot=field]]:mt-(--radio-gap)!': allowCustomItemPerRow && index + 1 >= itemsPerRow
            })}
            {...restOptionProps}
          />
        ))}
      </RadioGroup>
    </RHFFieldWrapper>
  );
};
